import * as THREE from "three";

import { BIOMES } from "../content/biomes";
import { pickupWorldColor } from "../content/pickup-identity";
import { POI_DEFS, type PoiKind, type PoiMarkerShape } from "../content/pois";
import { biomeBlendLabel } from "../world/biomes";
import { ChunkStreamer } from "../world/chunks";
import { sampleHeight } from "../world/terrain";
import { ALTITUDE_FLOOR_M, CHUNK_SIZE_M, EXTRACT_RADIUS_M } from "../simulation/constants";
import type { WorldSnapshot } from "../simulation/snapshot";
import { disposeObject } from "../core/scene-lifecycle";
import type { EnemyRoleId } from "../content/schemas";
import { AffixShipFx } from "./affix-ship-fx";
import { BuildingField } from "./buildings";
import { BoostTrail } from "./boost-trail";
import { applyEnemyVisual, createEnemyMesh, disposeEnemyMesh } from "./enemy-meshes";
import { ProjectileField } from "./projectile-meshes";
import { createAimReticle, createBlobShadow, createShipMesh } from "./ship-mesh";
import type { AudioEvent } from "../simulation/snapshot";
import type { CombatJuiceSample } from "../presentation/combat-juice";
import { CombatVfx } from "./combat-vfx";
import { TelegraphRenderer } from "./telegraphs";
import {
  createConcreteTexture,
  createMetalPbrMaps,
  setTextureAnisotropy,
  type MetalPbrMaps,
} from "./textures";
import { TerrainGround } from "./terrain-ground";
import { createTerrainAnnulus, updateTerrainAnnulus } from "./terrain-annulus";
import { atmosphereFor, type BiomeAtmosphere } from "./atmosphere";
import type { ResolvedQuality } from "./quality";

const TRAIL_COLORS: Record<string, number> = {
  trail_ember: 0xc45c26,
  trail_ion: 0x6ec6ff,
  trail_rust: 0xa67c52,
};

/** Oblique chase camera: landforms need a visible horizon-facing slope to read. */
const CAM_HEIGHT_ABOVE_M = 64;
const CAM_BACK_M = 140;
const CAM_FOV_DEG = 58;
const CAM_FAR_M = 1600;
// Terrain is a sampled mesh, while tactical rings/reticles are evaluated at
// arbitrary positions. Keep flat overlays decisively above interpolation and
// material displacement so they cannot z-fight or disappear into a slope.
const GROUND_OVERLAY_CLEARANCE_M = 1.2;

/** Planar speed (m/s) above which hull yaws to velocity; below, fall back to aim facing. */
const HULL_VELOCITY_FACING_MPS = 2.5;

type PlayerVisual = {
  x: number;
  y: number;
  altitude: number;
  hullFacing: number;
  aimFacing: number;
};

/** Industrial renderer for the tangent-patch expedition. */
export class WorldView {
  public readonly scene = new THREE.Scene();
  public readonly camera = new THREE.PerspectiveCamera(CAM_FOV_DEG, 1, 0.5, CAM_FAR_M);
  private playerMesh: THREE.Group;
  private readonly shadow: THREE.Mesh;
  private readonly reticle: THREE.Group;
  private readonly carrier: THREE.Group;
  private readonly terrain: TerrainGround;
  private readonly grid: THREE.GridHelper;
  private readonly hemi: THREE.HemisphereLight;
  private readonly sun: THREE.DirectionalLight;
  private readonly carrierLight: THREE.PointLight;
  private readonly enemyGroup = new THREE.Group();
  private readonly dropGroup = new THREE.Group();
  private readonly poiGroup = new THREE.Group();
  private readonly poiPool: THREE.Group[] = [];
  private readonly extractRing: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>;
  private readonly carrierBeacon: THREE.Mesh;
  private readonly boostTrail: BoostTrail;
  private readonly enemyPool: THREE.Group[] = [];
  private readonly enemyFlash = new Map<
    number,
    { prevHull: number; flashTimer: number; gen: number }
  >();
  private enemySyncGen = 0;
  private readonly telegraphs: TelegraphRenderer;
  private readonly affixFx: AffixShipFx;
  private readonly projectiles: ProjectileField;
  private readonly dropPool: THREE.Mesh[] = [];
  private readonly buildings: BuildingField;
  private streamerSeed = 0;
  private streamer: ChunkStreamer | null = null;
  private lastBiomeId = "";
  private readonly metalPbr: MetalPbrMaps;
  private readonly concreteTex: THREE.CanvasTexture;
  private readonly dangerRing: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>;
  private readonly combatVfx: CombatVfx;
  private frameId = "skimmer";
  private readonly prevPlayer: PlayerVisual = {
    x: 0,
    y: 0,
    altitude: 24,
    hullFacing: 0,
    aimFacing: 0,
  };
  private readonly currPlayer: PlayerVisual = {
    x: 0,
    y: 0,
    altitude: 24,
    hullFacing: 0,
    aimFacing: 0,
  };
  private readonly lastPlayerVel = { vx: 0, vy: 0 };
  private lastRenderMs = 0;
  private baseFov = CAM_FOV_DEG;
  private atmosphere: BiomeAtmosphere = atmosphereFor("ash_band");
  private quality: ResolvedQuality | null = null;
  private contactShadowQuality: ResolvedQuality["contactShadowQuality"] = "full";
  private readonly backgroundColor = new THREE.Color(0x3a4540);
  private readonly emptyJuice: CombatJuiceSample = {
    shake: 0,
    offsetX: 0,
    offsetY: 0,
    offsetZ: 0,
    fovKickDeg: 0,
    hitstopActive: false,
    boostLines: 0,
  };
  private lastDangerTier = -1;
  private lastExtractPhase = "";
  private lastCarrierNavActive = false;
  private frozenPresentationTimeSeconds: number | null = null;

  public constructor() {
    this.scene.background = this.backgroundColor;
    // Atmospheric depth hides the streaming-patch edge and separates ridges,
    // water and distant prop silhouettes without obscuring the ship.
    this.scene.fog = new THREE.Fog(0x3a4540, 230, 820);

    this.hemi = new THREE.HemisphereLight(0xb7a894, 0x1a1510, 0.42);
    this.scene.add(this.hemi);
    // A low, raking key light lets terrain normals and landforms read from
    // the chase camera; the old overhead light made every slope look flat.
    this.sun = new THREE.DirectionalLight(0xffe08a, 2.3);
    this.sun.position.set(140, 72, -120);
    this.scene.add(this.sun);

    this.metalPbr = createMetalPbrMaps(0x6a7076);
    this.concreteTex = createConcreteTexture(0x3a3834);

    this.terrain = new TerrainGround(this.scene);

    // Subtle sector grid (low contrast) over textured ground.
    this.grid = new THREE.GridHelper(480, 48, 0x4a4034, 0x2a241c);
    const gridMat = this.grid.material as THREE.Material | THREE.Material[];
    if (Array.isArray(gridMat)) {
      for (const m of gridMat) {
        m.transparent = true;
        m.opacity = 0.07;
      }
    } else {
      gridMat.transparent = true;
      gridMat.opacity = 0.07;
    }
    this.scene.add(this.grid);

    this.playerMesh = createShipMesh(this.frameId);
    this.scene.add(this.playerMesh);

    this.affixFx = new AffixShipFx();
    this.affixFx.captureShip(this.playerMesh);

    this.shadow = createBlobShadow();
    this.scene.add(this.shadow);

    this.reticle = createAimReticle();
    this.scene.add(this.reticle);

    const carrierBuilt = createCarrierMesh(this.metalPbr, this.concreteTex);
    this.carrier = carrierBuilt.root;
    this.carrierBeacon = carrierBuilt.beacon;
    this.scene.add(this.carrier);
    this.carrierLight = new THREE.PointLight(0xd4b06a, 1.8, 38, 2);
    this.carrierLight.position.set(0, 10, 0);
    this.carrier.add(this.carrierLight);

    this.extractRing = createTerrainAnnulus({ color: 0xd4b06a, opacity: 0.18 });
    this.scene.add(this.extractRing);

    this.dangerRing = createTerrainAnnulus({ color: 0xc45c26, opacity: 0.2 });
    this.scene.add(this.dangerRing);

    this.buildings = new BuildingField(this.scene);
    this.telegraphs = new TelegraphRenderer(this.scene);
    this.combatVfx = new CombatVfx(this.scene);
    this.projectiles = new ProjectileField(this.scene);
    this.scene.add(this.enemyGroup, this.dropGroup, this.poiGroup);

    this.boostTrail = new BoostTrail(this.scene);
    this.grid.visible = false;

    this.camera.position.set(0, 24 + CAM_HEIGHT_ABOVE_M, CAM_BACK_M);
  }

  public sync(snap: WorldSnapshot): void {
    if (snap.player.frameId !== this.frameId) {
      this.swapPlayerMesh(snap.player.frameId);
    }

    if (!this.streamer || this.streamerSeed !== snap.seed) {
      this.streamer = new ChunkStreamer(snap.seed);
      this.streamerSeed = snap.seed;
    }

    const hullFacing = resolveHullFacing(
      snap.player.vx,
      snap.player.vy,
      snap.player.facing,
      this.currPlayer.hullFacing,
    );

    copyPlayerVisual(this.prevPlayer, this.currPlayer);
    this.currPlayer.x = snap.player.x;
    this.currPlayer.y = snap.player.y;
    this.currPlayer.altitude = snap.player.altitude;
    this.currPlayer.hullFacing = hullFacing;
    this.currPlayer.aimFacing = snap.player.facing;
    this.lastPlayerVel.vx = snap.player.vx;
    this.lastPlayerVel.vy = snap.player.vy;

    const biomeId =
      snap.biome || biomeBlendLabel(snap.seed, snap.player.x, snap.player.y, CHUNK_SIZE_M);
    const biome = BIOMES[biomeId];
    if (biomeId !== this.lastBiomeId) {
      this.applyBiomeAtmosphere(biomeId, biome.fogColor);
      this.lastBiomeId = biomeId;
    }
    this.applyBossAtmosphereTint(biome.fogColor, snap.bossAtmosphere);
    this.terrain.sync(snap.seed, snap.player.x, snap.player.y);
    this.grid.position.set(
      snap.player.x,
      sampleHeight(snap.seed, snap.player.x, snap.player.y) + 0.02,
      snap.player.y,
    );
    const padY = sampleHeight(snap.seed, snap.carrierX, snap.carrierY);
    this.carrier.position.set(snap.carrierX, padY, snap.carrierY);
    updateTerrainAnnulus(this.extractRing, {
      seed: snap.seed,
      centerX: snap.carrierX,
      centerZ: snap.carrierY,
      radius: EXTRACT_RADIUS_M,
      thickness: 0.45,
    });
    const extractRelevant = Boolean(snap.carrierNav?.active) || snap.phase === "extracting";
    const extractMat = this.extractRing.material as THREE.MeshBasicMaterial;
    if (extractRelevant !== this.lastCarrierNavActive || snap.phase !== this.lastExtractPhase) {
      extractMat.opacity = extractRelevant ? (snap.phase === "extracting" ? 0.55 : 0.38) : 0.16;
      extractMat.color.setHex(snap.phase === "extracting" ? 0xffe08a : 0xd4b06a);
      this.lastCarrierNavActive = extractRelevant;
      this.lastExtractPhase = snap.phase;
    }
    const pulse = 0.85 + 0.15 * Math.sin(snap.simTime * (extractRelevant ? 4.2 : 1.6));
    this.carrierBeacon.scale.setScalar(pulse);
    const beaconMat = this.carrierBeacon.material as THREE.MeshStandardMaterial;
    beaconMat.emissiveIntensity = extractRelevant ? 1.6 : 0.75;

    // Edge-of-zone danger cue: stronger ring in hotter bands.
    if (snap.dangerTier !== this.lastDangerTier) {
      const dangerOpacity = snap.dangerTier === 3 ? 0.16 : snap.dangerTier === 2 ? 0.09 : 0.035;
      const dangerColor =
        snap.dangerTier === 3 ? 0xc45c26 : snap.dangerTier === 2 ? 0xb05a3a : 0x6a7a52;
      const ringMat = this.dangerRing.material as THREE.MeshBasicMaterial;
      ringMat.opacity = dangerOpacity;
      ringMat.color.setHex(dangerColor);
      this.lastDangerTier = snap.dangerTier;
    }
    updateTerrainAnnulus(this.dangerRing, {
      seed: snap.seed,
      centerX: Math.round(snap.player.x / 4) * 4,
      centerZ: Math.round(snap.player.y / 4) * 4,
      radius: 40 * (0.85 + snap.dangerTier * 0.12),
      thickness: 0.3,
    });

    const chunks = this.streamer.ensureAround(snap.player.x, snap.player.y, 2);
    this.buildings.sync(chunks, snap.seed);
    this.syncEnemies(snap);
    this.telegraphs.sync(snap, snap.enemies);
    this.affixFx.sync(snap);

    this.projectiles.sync(snap.projectiles, this.affixFx.projectileTint());

    this.syncPool(
      this.dropGroup,
      this.dropPool,
      snap.drops,
      () =>
        new THREE.Mesh(
          new THREE.OctahedronGeometry(0.7),
          new THREE.MeshStandardMaterial({
            color: 0xd0d4d8,
            map: this.metalPbr.map,
            roughnessMap: this.metalPbr.roughnessMap,
            metalnessMap: this.metalPbr.metalnessMap,
            emissive: 0x3a5a20,
            emissiveIntensity: 1.2,
            metalness: 0.4,
            roughness: 0.45,
          }),
        ),
      (mesh, drop) => {
        mesh.position.set(drop.x, sampleHeight(snap.seed, drop.x, drop.y) + 2.5, drop.y);
        const mat = mesh.material as THREE.MeshStandardMaterial;
        mat.emissive.setHex(pickupWorldColor(drop.kind, drop.payloadId));
        mat.emissiveIntensity =
          drop.kind === "salvage" ? 1.05 : drop.kind === "elite_core" ? 1.55 : 1.35;
        mesh.visible = true;
      },
    );

    this.syncPois(snap);

    const trailColor = TRAIL_COLORS[snap.cosmeticsTrailId] ?? 0xc45c26;
    const trailBoost = this.affixFx.trailBoost(trailColor);
    const planar = Math.hypot(snap.player.vx, snap.player.vy);
    this.boostTrail.sync(
      snap.player.x,
      snap.player.y,
      snap.player.altitude,
      snap.player.vx,
      snap.player.vy,
      trailBoost.color,
      planar,
    );
  }

  /** Spawn muzzle / impact / kill pops from sim audio events. */
  public ingestCombatEvents(
    events: readonly AudioEvent[],
    player: { x: number; y: number; altitude: number; facing: number },
  ): void {
    this.combatVfx.ingest(events, player);
  }

  public renderFrame(
    interpolation: number,
    reducedMotion: boolean,
    juice: CombatJuiceSample | null = null,
  ): void {
    const now =
      this.frozenPresentationTimeSeconds == null
        ? performance.now()
        : this.frozenPresentationTimeSeconds * 1000;
    const dt = this.lastRenderMs > 0 ? Math.min(0.05, (now - this.lastRenderMs) / 1000) : 1 / 60;
    this.lastRenderMs = now;
    this.terrain.animate(now / 1000);

    const t = reducedMotion ? 1 : interpolation;
    const x = this.prevPlayer.x + (this.currPlayer.x - this.prevPlayer.x) * t;
    const y = this.prevPlayer.y + (this.currPlayer.y - this.prevPlayer.y) * t;
    const alt =
      this.prevPlayer.altitude + (this.currPlayer.altitude - this.prevPlayer.altitude) * t;
    const hullFacing = lerpAngle(this.prevPlayer.hullFacing, this.currPlayer.hullFacing, t);
    const aimFacing = lerpAngle(this.prevPlayer.aimFacing, this.currPlayer.aimFacing, t);

    // Render defensively against an interpolated/stale snapshot: the ship can
    // never appear below the same terrain surface used by simulation.
    const ground = sampleHeight(this.streamerSeed, x, y);
    const shipAltitude = Math.max(alt, ground + ALTITUDE_FLOOR_M);
    this.playerMesh.position.set(x, shipAltitude, y);
    this.playerMesh.rotation.y = -hullFacing;

    this.updateContactShadow(x, y, shipAltitude, hullFacing);

    this.reticle.position.set(x, ground + GROUND_OVERLAY_CLEARANCE_M, y);
    this.reticle.rotation.y = -aimFacing;

    const heightAbove = reducedMotion ? CAM_HEIGHT_ABOVE_M + 6 : CAM_HEIGHT_ABOVE_M;
    const camBack = reducedMotion ? CAM_BACK_M + 4 : CAM_BACK_M;
    const camHeight = shipAltitude + heightAbove;
    const shake = juice && !reducedMotion ? juice : null;
    const ox = shake?.offsetX ?? 0;
    const oy = shake?.offsetY ?? 0;
    const oz = shake?.offsetZ ?? 0;
    this.camera.position.set(x + ox, camHeight + oy, y + camBack + oz);
    this.camera.lookAt(x + ox * 0.35, shipAltitude - 6, y + oz * 0.35);

    const fov = this.baseFov + (shake?.fovKickDeg ?? 0);
    if (Math.abs(this.camera.fov - fov) > 0.01) {
      this.camera.fov = fov;
      this.camera.updateProjectionMatrix();
    }

    this.combatVfx.setReducedMotion(reducedMotion);
    this.combatVfx.update(
      dt,
      {
        x,
        y,
        altitude: shipAltitude,
        facing: aimFacing,
        vx: this.lastPlayerVel.vx,
        vy: this.lastPlayerVel.vy,
      },
      shake ?? this.emptyJuice,
    );

    this.affixFx.place(x, y, shipAltitude, hullFacing);
  }

  public resize(width: number, height: number): void {
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  public freezePresentationAt(timeSeconds: number): void {
    this.frozenPresentationTimeSeconds = timeSeconds;
  }

  public terrainStats(): ReturnType<TerrainGround["stats"]> {
    return this.terrain.stats();
  }

  public applyQuality(quality: ResolvedQuality, envMap: THREE.Texture | null): void {
    this.quality = quality;
    this.terrain.setQuality(quality.id);
    this.contactShadowQuality = quality.contactShadowQuality;
    setTextureAnisotropy(quality.textureAnisotropy);
    this.scene.environment = quality.envMap ? envMap : null;
    this.setEnvIntensityOnScene(quality.envIntensity);
    this.setTextureAnisotropyOnScene(quality.textureAnisotropy);
    this.carrierLight.visible = quality.carrierPointLight;
    this.carrierLight.intensity = quality.carrierPointLight
      ? this.atmosphere.carrierLightIntensity
      : 0;
    this.combatVfx.setQuality(quality.vfxBudget, quality.speedLineCount);
  }

  public dispose(): void {
    this.combatVfx.dispose();
    this.boostTrail.dispose();
    this.projectiles.dispose();
    this.telegraphs.dispose();
    this.affixFx.dispose();
    for (const group of this.enemyPool) disposeEnemyMesh(group);
    this.enemyPool.length = 0;
    this.enemyFlash.clear();
    this.buildings.dispose();
    this.terrain.dispose();
    this.metalPbr.map.dispose();
    this.metalPbr.roughnessMap.dispose();
    this.metalPbr.metalnessMap.dispose();
    this.concreteTex.dispose();
    this.scene.environment = null;
    disposeObject(this.scene);
  }

  private applyBiomeAtmosphere(biomeId: string, fogColor: number): void {
    this.atmosphere = atmosphereFor(biomeId);
    this.hemi.color.setHex(this.atmosphere.hemiSky);
    this.hemi.groundColor.setHex(this.atmosphere.hemiGround);
    this.hemi.intensity = this.atmosphere.hemiIntensity;
    this.sun.color.setHex(this.atmosphere.sunColor);
    this.sun.intensity = this.atmosphere.sunIntensity;
    this.sun.position.fromArray(this.atmosphere.sunPosition);
    const fog = this.scene.fog;
    if (fog instanceof THREE.Fog) {
      fog.color.setHex(fogColor);
    }
    this.backgroundColor.setHex(fogColor).offsetHSL(0, 0, this.atmosphere.backgroundLift);
    if (this.quality?.carrierPointLight) {
      this.carrierLight.color.setHex(this.atmosphere.carrierLightColor);
      this.carrierLight.intensity = this.atmosphere.carrierLightIntensity;
    }
  }

  /** Warm fog/sky shift while inside or fighting a boss arena. */
  private applyBossAtmosphereTint(baseFog: number, intensity: number): void {
    const fog = this.scene.fog;
    if (!(fog instanceof THREE.Fog)) return;
    fog.color.setHex(baseFog);
    this.backgroundColor.setHex(baseFog).offsetHSL(0, 0, this.atmosphere.backgroundLift);
    if (intensity <= 0.02) return;
    const boss = new THREE.Color(0xc45c26);
    fog.color.lerp(boss, intensity * 0.38);
    this.backgroundColor.lerp(new THREE.Color(0x3a1810), intensity * 0.22);
  }

  private setEnvIntensityOnScene(intensity: number): void {
    this.scene.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh) return;
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const material of materials) {
        if (material instanceof THREE.MeshStandardMaterial) {
          material.envMapIntensity = intensity;
        }
      }
    });
  }

  private setTextureAnisotropyOnScene(anisotropy: number): void {
    this.scene.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh) return;
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const material of materials) {
        if (!(material instanceof THREE.MeshStandardMaterial)) continue;
        for (const texture of [material.map, material.roughnessMap, material.metalnessMap]) {
          if (texture) texture.anisotropy = anisotropy;
        }
      }
    });
  }

  private swapPlayerMesh(frameId: string): void {
    this.scene.remove(this.playerMesh);
    disposeObject(this.playerMesh);
    this.frameId = frameId;
    this.playerMesh = createShipMesh(frameId);
    this.scene.add(this.playerMesh);
    this.setEnvIntensityOnScene(this.quality?.envIntensity ?? 0);
    this.affixFx.captureShip(this.playerMesh);
  }

  private updateContactShadow(x: number, z: number, altitude: number, hullFacing: number): void {
    const clearance = Math.max(0, altitude - sampleHeight(this.streamerSeed, x, z));
    const full = this.contactShadowQuality === "full";
    const baseScale = (full ? 0.98 : 0.9) + clearance / (full ? 76 : 100);
    const soft = clamp(1 - clearance / (full ? 150 : 110), 0.1, 0.88);
    const stretch = 1 + clamp(clearance / (full ? 185 : 260), 0, full ? 0.46 : 0.18);

    this.shadow.position.set(x, sampleHeight(this.streamerSeed, x, z) + 0.28, z);
    this.shadow.rotation.y = -hullFacing;
    this.shadow.scale.set(baseScale * stretch, baseScale, 1);
    const mat = this.shadow.material as THREE.MeshBasicMaterial;
    mat.opacity = soft;
  }

  private syncEnemies(snap: WorldSnapshot): void {
    const enemies = snap.enemies;
    const dt = 1 / 60;
    const seen = this.enemyFlash;
    this.enemySyncGen += 1;
    const gen = this.enemySyncGen;
    while (this.enemyPool.length < enemies.length) {
      const group = createEnemyMesh("scout", this.metalPbr.map);
      this.enemyPool.push(group);
      this.enemyGroup.add(group);
    }

    for (let i = 0; i < this.enemyPool.length; i += 1) {
      const group = this.enemyPool[i]!;
      const enemy = enemies[i];
      if (!enemy) {
        group.visible = false;
        continue;
      }

      if ((group.userData.role as EnemyRoleId | undefined) !== enemy.role) {
        this.enemyGroup.remove(group);
        disposeEnemyMesh(group);
        const replacement = createEnemyMesh(enemy.role, this.metalPbr.map);
        replacement.userData.role = enemy.role;
        this.enemyPool[i] = replacement;
        this.enemyGroup.add(replacement);
        seen.delete(enemy.id);
      }

      const mesh = this.enemyPool[i]!;
      mesh.visible = true;
      let flash = seen.get(enemy.id);
      if (!flash) {
        flash = { prevHull: enemy.hull, flashTimer: 0, gen };
        seen.set(enemy.id, flash);
      }
      flash.flashTimer = applyEnemyVisual(
        mesh,
        enemy,
        flash.prevHull,
        flash.flashTimer,
        dt,
        snap.bounty?.targetId === enemy.id,
      );
      flash.prevHull = enemy.hull;
      flash.gen = gen;
    }

    if ((gen & 31) === 0 && seen.size > enemies.length + 8) {
      for (const [id, flash] of seen) {
        if (flash.gen !== gen) seen.delete(id);
      }
    }
  }

  private syncPool<T>(
    group: THREE.Group,
    pool: THREE.Mesh[],
    items: readonly T[],
    create: () => THREE.Mesh,
    apply: (mesh: THREE.Mesh, item: T) => void,
  ): void {
    while (pool.length < items.length) {
      const mesh = create();
      pool.push(mesh);
      group.add(mesh);
    }
    for (let i = 0; i < pool.length; i += 1) {
      const mesh = pool[i]!;
      const item = items[i];
      if (!item) {
        mesh.visible = false;
        continue;
      }
      apply(mesh, item);
    }
  }

  private syncPois(snap: WorldSnapshot): void {
    while (this.poiPool.length < snap.pois.length) {
      const marker = createPoiMarkerGroup();
      this.poiPool.push(marker);
      this.poiGroup.add(marker);
    }
    for (let i = 0; i < this.poiPool.length; i += 1) {
      const group = this.poiPool[i]!;
      const poi = snap.pois[i];
      if (!poi || poi.cleared) {
        group.visible = false;
        continue;
      }
      group.visible = true;
      group.position.set(0, 0, 0);
      const pulse =
        poi.kind === "boss_arena" && snap.bossAtmosphere > 0.05 ? snap.bossAtmosphere : 0;
      applyPoiMarker(
        group,
        poi.kind,
        poi.radius,
        pulse,
        snap.simTime,
        snap.settings.reducedMotion,
        snap.seed,
        poi.x,
        poi.y,
      );
    }
  }
}

function createCarrierMesh(
  metal: MetalPbrMaps,
  concrete: THREE.Texture,
): { root: THREE.Group; beacon: THREE.Mesh } {
  const root = new THREE.Group();
  root.name = "carrier";

  const deck = new THREE.Mesh(
    new THREE.CylinderGeometry(14, 18, 3.4, 10),
    new THREE.MeshStandardMaterial({
      color: 0x5a6066,
      map: metal.map,
      roughnessMap: metal.roughnessMap,
      metalnessMap: metal.metalnessMap,
      metalness: 0.72,
      roughness: 0.38,
      envMapIntensity: 0.42,
    }),
  );
  deck.position.y = 1.7;

  const pad = new THREE.Mesh(
    new THREE.BoxGeometry(12, 1.1, 12),
    new THREE.MeshStandardMaterial({
      color: 0x3a3834,
      map: concrete,
      metalness: 0.35,
      roughness: 0.75,
      envMapIntensity: 0.1,
    }),
  );
  pad.position.y = 3.5;

  // Landing H marking — readable from the chase camera.
  const markMat = new THREE.MeshBasicMaterial({
    color: 0xd4b06a,
    transparent: true,
    opacity: 0.85,
    depthWrite: false,
  });
  const markH = new THREE.Mesh(new THREE.BoxGeometry(7.5, 0.12, 1.1), markMat);
  markH.position.y = 4.15;
  const markV = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.12, 7.5), markMat.clone());
  markV.position.y = 4.15;

  const lightMat = new THREE.MeshStandardMaterial({
    color: 0x8a7040,
    emissive: 0xd4b06a,
    emissiveIntensity: 1.45,
    metalness: 0.2,
    roughness: 0.45,
    toneMapped: false,
  });
  const padLights: THREE.Mesh[] = [];
  for (const [lx, lz] of [
    [-5.2, -5.2],
    [5.2, -5.2],
    [-5.2, 5.2],
    [5.2, 5.2],
  ] as const) {
    const light = new THREE.Mesh(new THREE.BoxGeometry(1.1, 0.35, 1.1), lightMat.clone());
    light.position.set(lx, 4.2, lz);
    padLights.push(light);
  }

  const mast = new THREE.Mesh(
    new THREE.BoxGeometry(1.6, 10, 1.6),
    new THREE.MeshStandardMaterial({
      color: 0x4a5056,
      map: metal.map,
      roughnessMap: metal.roughnessMap,
      metalnessMap: metal.metalnessMap,
      metalness: 0.65,
      roughness: 0.45,
      emissive: 0x3a2808,
      emissiveIntensity: 0.65,
      envMapIntensity: 0.38,
    }),
  );
  mast.position.set(-7.5, 7, 0);

  const beacon = new THREE.Mesh(
    new THREE.CylinderGeometry(0.55, 0.85, 2.4, 8),
    new THREE.MeshStandardMaterial({
      color: 0xd4b06a,
      emissive: 0xffc060,
      emissiveIntensity: 1.4,
      metalness: 0.35,
      roughness: 0.35,
      toneMapped: false,
    }),
  );
  beacon.position.set(-7.5, 13.2, 0);
  beacon.name = "carrierBeacon";

  // Side hull stripe so the pad reads as a ship, not a rock.
  const stripe = new THREE.Mesh(
    new THREE.BoxGeometry(22, 0.5, 1.2),
    new THREE.MeshBasicMaterial({ color: 0xb08a3a, transparent: true, opacity: 0.7 }),
  );
  stripe.position.set(0, 2.4, 0);

  root.add(deck, pad, markH, markV, mast, beacon, stripe, ...padLights);
  return { root, beacon };
}

function createPoiMarkerGroup(): THREE.Group {
  const root = new THREE.Group();
  root.name = "poi";

  const ring = createTerrainAnnulus({ color: 0x6a7a82, opacity: 0.34 });
  ring.name = "ring";

  const inner = createTerrainAnnulus({ color: 0x8a9aa2, opacity: 0.22 });
  inner.name = "inner";

  const marker = new THREE.Group();
  marker.name = "marker";

  root.add(ring, inner, marker);
  root.userData.kind = "" as PoiKind | "";
  return root;
}

function applyPoiMarker(
  group: THREE.Group,
  kind: PoiKind,
  radius: number,
  pulse = 0,
  simTime = 0,
  reducedMotion = false,
  seed = 0,
  centerX = 0,
  centerZ = 0,
): void {
  const def = POI_DEFS[kind];
  const ring = group.getObjectByName("ring") as THREE.Mesh<
    THREE.BufferGeometry,
    THREE.MeshBasicMaterial
  >;
  const inner = group.getObjectByName("inner") as THREE.Mesh<
    THREE.BufferGeometry,
    THREE.MeshBasicMaterial
  >;
  const marker = group.getObjectByName("marker") as THREE.Group;

  const pulseScale = pulse > 0 && !reducedMotion ? 1 + 0.035 * Math.sin(simTime * 7) * pulse : 1;
  updateTerrainAnnulus(ring, {
    seed,
    centerX,
    centerZ,
    radius: radius * pulseScale,
    thickness: 0.45,
  });
  updateTerrainAnnulus(inner, {
    seed,
    centerX,
    centerZ,
    radius: radius * 0.62 * pulseScale,
    thickness: 0.3,
  });
  marker.position.set(centerX, sampleHeight(seed, centerX, centerZ) + 2.2, centerZ);
  const ringMat = ring.material as THREE.MeshBasicMaterial;
  const innerMat = inner.material as THREE.MeshBasicMaterial;
  ringMat.color.setHex(def.color);
  innerMat.color.setHex(def.accent);
  if (pulse > 0) {
    ringMat.opacity = Math.min(0.6, 0.34 + 0.26 * pulse);
    innerMat.opacity = Math.min(0.45, 0.22 + 0.23 * pulse);
    ringMat.color.offsetHSL(0, 0.05, 0.08 * pulse);
  } else {
    ringMat.opacity = 0.34;
    innerMat.opacity = 0.22;
  }

  if (group.userData.kind !== kind) {
    while (marker.children.length > 0) {
      const child = marker.children[0]!;
      marker.remove(child);
      disposeObject(child);
    }
    marker.add(createPoiGlyph(def.marker, def.color, def.accent));
    group.userData.kind = kind;
  }
}

function createPoiGlyph(shape: PoiMarkerShape, color: number, accent: number): THREE.Object3D {
  const bodyMat = new THREE.MeshStandardMaterial({
    color,
    emissive: accent,
    emissiveIntensity: 0.55,
    metalness: 0.45,
    roughness: 0.5,
  });
  const accentMat = new THREE.MeshBasicMaterial({
    color: accent,
    transparent: true,
    opacity: 0.9,
    depthWrite: false,
  });

  switch (shape) {
    case "diamond": {
      const gem = new THREE.Mesh(new THREE.OctahedronGeometry(1.6), bodyMat);
      gem.rotation.y = Math.PI / 4;
      return gem;
    }
    case "mast": {
      const root = new THREE.Group();
      const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.4, 4.2, 6), bodyMat);
      pole.position.y = 1.2;
      const dish = new THREE.Mesh(new THREE.ConeGeometry(1.3, 1.1, 5), accentMat);
      dish.position.y = 3.4;
      root.add(pole, dish);
      return root;
    }
    case "triangle": {
      const cone = new THREE.Mesh(new THREE.ConeGeometry(1.8, 2.6, 3), bodyMat);
      cone.rotation.y = Math.PI;
      return cone;
    }
    case "crate": {
      const root = new THREE.Group();
      const box = new THREE.Mesh(new THREE.BoxGeometry(2.2, 1.6, 2.2), bodyMat);
      const lid = new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.25, 2.4), accentMat);
      lid.position.y = 0.95;
      root.add(box, lid);
      return root;
    }
    case "star": {
      const root = new THREE.Group();
      const core = new THREE.Mesh(new THREE.TetrahedronGeometry(1.5), bodyMat);
      core.position.y = 0.4;
      const armA = new THREE.Mesh(new THREE.BoxGeometry(4.2, 0.35, 0.55), accentMat);
      const armB = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.35, 4.2), accentMat);
      armA.position.y = 0.3;
      armB.position.y = 0.3;
      root.add(core, armA, armB);
      return root;
    }
    default:
      return new THREE.Mesh(new THREE.SphereGeometry(1.2, 8, 8), bodyMat);
  }
}

function copyPlayerVisual(dst: PlayerVisual, src: PlayerVisual): void {
  dst.x = src.x;
  dst.y = src.y;
  dst.altitude = src.altitude;
  dst.hullFacing = src.hullFacing;
  dst.aimFacing = src.aimFacing;
}

/** Hull yaws to planar velocity while moving; near-stationary falls back to aim facing. */
function resolveHullFacing(
  vx: number,
  vy: number,
  aimFacing: number,
  lastHullFacing: number,
): number {
  const speed = Math.hypot(vx, vy);
  if (speed >= HULL_VELOCITY_FACING_MPS) {
    return Math.atan2(vy, vx);
  }
  if (Number.isFinite(aimFacing)) return aimFacing;
  return lastHullFacing;
}

function lerpAngle(a: number, b: number, t: number): number {
  let delta = b - a;
  while (delta > Math.PI) delta -= Math.PI * 2;
  while (delta < -Math.PI) delta += Math.PI * 2;
  return a + delta * t;
}

function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}
